<template lang="pug">
    div
        ElSkeleton(
            v-if="retrievePointsCount == 0"
            style="height: 100%; display: flex;"
            animated
        )
            ElSkeletonItem(slot="template" style="height: 100%")

        LMap(v-if="retrievePointsCount > 0" ref="map" @ready="initializeMap()" :options="mapOptions")
            LTileLayer(:url="url")
            
            LCircle(
                v-for="(point, index) in points.filter(radiusIsCorrect)"
                :visible="shouldBeVisible(formIndex, getSelectedPoint(point))"
                :key="`circle-${getSelectedPoint(point)}`"
                :lat-lng="[point.latitude, point.longitude]"
                :radius="getRadiusFromAPoint(point)"
                :color="getCircleColorFor(point)"
                :fillColor="getCircleColorFor(point)"
                :opacity="pointOpacity(getSelectedPoint(point))"
            )
            LMarker(
                v-for="(point, index) in points.filter(pointIsCorrect)"
                :visible="shouldBeVisible(formIndex, getSelectedPoint(point))"
                :key="`point-${getSelectedPoint(point)}`"
                :lat-lng="[point.latitude, point.longitude]"
                :icon="getMarkerIconFor(point, getSelectedPoint(point))"
                :opacity="pointOpacity(getSelectedPoint(point))"
                @click="handleClickOnMarker(point)"
            )

            //- new point in the form
            LCircle(
                v-if="formAction && radiusIsCorrect(formPoint)"
                :lat-lng="[formPoint.latitude, formPoint.longitude]"
                :radius="getRadiusFromAPoint(formPoint)"
                :color="getCircleColorFor(formPoint, formAction)"
                :fillColor="getCircleColorFor(formPoint, formAction)"
            )
            LMarker(
                v-if="formAction && this.pointIsCorrect(formPoint)"
                :lat-lng="[formPoint.latitude, formPoint.longitude]"
                :icon="getMarkerIcon('big', getMarkerColor(formPoint, formAction))"
            )
    </template>

<script>
import { EMPTY_POINT } from '@/store/ui/georadar/helpers'
import { mapMutations, mapState } from 'vuex'
import { isEqual } from 'lodash'

import { LCircle, LMap, LMarker, LTileLayer } from 'vue2-leaflet'
import L from 'leaflet'
import 'leaflet-contextmenu'
import { GeoSearchControl, OpenStreetMapProvider } from 'leaflet-geosearch'
import { Icon } from 'leaflet'

import 'leaflet/dist/leaflet.css'
import 'leaflet-geosearch/assets/css/leaflet.css'
import 'leaflet-contextmenu/dist/leaflet.contextmenu.css'

delete Icon.Default.prototype._getIconUrl
Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png')
})

// LazyLoad geoJson
const allCountriesGeojson = () => import('@/utils/allCountriesGeojson.js')

export default {
    name: 'GeoradarMap',

    components: {
        LCircle,
        LMap,
        LMarker,
        LTileLayer
    },

    data() {
        return {
            url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}',
            mapOptions: {
                maxBounds: [
                    [90, -200],
                    [-90, 200]
                ],
                minZoom: 2,
                contextmenu: true,
                contextmenuWidth: 140,
                contextmenuItems: [
                    {
                        text: 'Set a position',
                        callback: this.setNewPosition
                    }
                ],
                attributionControl: false
            }
        }
    },

    watch: {
        focusedPoint: {
            handler({ index, shouldFly, shouldFlyEvenIfInMap }) {
                if (index != null && shouldFly) {
                    const point = this.points[index]
                    if (this.pointIsCorrect(point) && (shouldFlyEvenIfInMap || this.isOutOfTheScreenMap(point))) {
                        this.flyToPoint(point)
                    }
                }
            },
            deep: true
        },
        formPoint: {
            handler(point) {
                if (this.pointIsCorrect(point) && this.isOutOfTheScreenMap(point)) {
                    this.flyToPoint(point)
                }
            },
            deep: true
        }
    },

    computed: {
        ...mapState('data/georadar', {
            points: state => state.unSavedPoints,
            savedPoints: state => state.savedPoints,
            retrievePointsCount: state => state.retrievePointsCount
        }),
        ...mapState('ui/georadar', {
            formIndex: state => state.form.index,
            formPoint: state => state.form.point,
            formAction: state => state.form.action,
            focusedPoint: state => state.focusedPoint
        }),
        ...mapState('account', {
            accountCountry: state => state.country
        })
    },

    methods: {
        ...mapMutations('ui/georadar', ['setFocusedPoint', 'setFormAction', 'setForm', 'setFormUpdateInfos']),

        getMarkerIconFor(point, index) {
            const size = this.isFocused(index) ? 'big' : 'small'
            return this.getMarkerIcon(size, this.getMarkerColor(point))
        },

        getMarkerIcon(size = 'small', color = 'red') {
            const iconSize = {
                small: [31, 46],
                big: [46, 69]
            }
            const iconAnchor = {
                small: [15.5, 42],
                big: [23, 63]
            }
            return new Icon({
                iconUrl: require(`@/assets/img/map/marker_${size}_${color}.png`),
                iconSize: iconSize[size],
                iconAnchor: iconAnchor[size]
            })
        },

        getMarkerColor(point, defaultAction) {
            const colors = {
                create: 'green',
                update: 'orange',
                delete: 'red'
            }
            return colors[point.action || defaultAction] || 'blue'
        },

        getCircleColorFor(point, defaultAction) {
            const colors = {
                create: '#77b22a', // green
                update: '#e99a2b', // orange
                delete: '#ff0000' // red
            }
            return colors[point.action || defaultAction] || '#3388ff' // blue
        },

        isFocused(index) {
            return index === this.focusedPoint.index || index == this.formIndex
        },

        pointOpacity(index) {
            return this.formAction || (this.focusedPoint.index != null && this.focusedPoint.index != index) ? 0.6 : 1
        },

        initializeMap() {
            const map = this.$refs.map.mapObject
            this.initializeGeoSearchBar(map)
            this.centerMapOnAccountCountry(map)
        },

        initializeGeoSearchBar(map) {
            const provider = new OpenStreetMapProvider()
            const searchControl = new GeoSearchControl({
                provider,
                style: 'bar'
            })
            map.addControl(searchControl)
        },

        async centerMapOnAccountCountry(map) {
            const countries = await allCountriesGeojson()
            const country = countries.default.find(c => c.code2 === this.accountCountry.code2)
            const polygon = L.polygon(country.coordinates)
            map.flyToBounds(polygon.getBounds())
        },

        getRadiusFromAPoint({ radius }) {
            const radiusInKilometer = new Number(radius)
            return (radiusInKilometer * 1000) / 2
        },

        setNewPosition({ latlng }) {
            this.setForm({ ...EMPTY_POINT, latitude: latlng.lat, longitude: latlng.lng })
            this.setFormAction('create')
        },

        pointIsCorrect({ latitude, longitude }) {
            return (
                !isNaN(latitude) &&
                !isNaN(longitude) &&
                latitude != '' &&
                latitude != null &&
                longitude != '' &&
                longitude != null &&
                latitude >= -90 &&
                latitude <= 90 &&
                longitude >= -180 &&
                longitude <= 180
            )
        },

        radiusIsCorrect({ latitude, longitude, radius }) {
            return this.pointIsCorrect({ latitude, longitude }) && !isNaN(radius)
        },

        flyToPoint({ latitude, longitude }) {
            const map = this.$refs.map.mapObject
            map.panTo([latitude, longitude], undefined)
        },

        shouldBeVisible(formIndex, index) {
            return formIndex !== index
        },

        handleClickOnMarker(mapPoint) {
            const index = this.points.findIndex((point) => isEqual(point, mapPoint))
            if (this.isFocused(index) && !this.formAction) {
                this.setFocusedPoint({ index: null, shouldFly: false, shouldScroll: false })
            } else {
                if (this.formAction) {
                    this.setFormAction(null)
                    this.setFormUpdateInfos({ index: null })
                }
                this.setFocusedPoint({ index, shouldFly: true, shouldScroll: true })
            }
        },

        isOutOfTheScreenMap({ latitude, longitude }) {
            const mapBounds = this.$refs.map.mapObject.getBounds().pad(-0.1)
            const point = new L.latLng(latitude, longitude)
            return !mapBounds.contains(point)
        },

        getSelectedPoint(selectedPoint) {
            return this.points.findIndex((point) => isEqual(point, selectedPoint))
        }
    }
}
</script>
